/**
 * WHAT AN ECH DOCUMENT PUTS IN THE AIRTABLE COLUMNS.
 *
 * `visualSpec` was written against the repo's own hand-built blocks, so every map in it is
 * keyed on a repo TYPE — `hero`, `cta`, `listing`, `amenities`. A document assembled from
 * Layne's Marketing Cloud blocks is typed `sfmc_<contentId>` (`sfmc_1028542`), so NONE of
 * those maps can hit, and the photo, button and module columns come out empty on every ECH
 * build. Measured 2026-08-25 on Katy's real intake row `recaDi7q6f6phIzPt`: Block Order,
 * Communities, Copy Input, Submission ID, UTMs and Est. Reach all landed, while every image
 * column, both CTA columns and Module 1/2/3 were blank.
 *
 * **This is NOT the bug that was fixed this morning, and fixing that one did not fix this.**
 * `049ff77` made the block document ARRIVE (it was being dropped whenever the requester used
 * the Inspector rather than the undo stack). This is the next gate down: the document arrives
 * and the mapper cannot read it. Both produce Katy's identical sentence — "no photo, button,
 * url or copy" — which is why one masked the other for a week.
 *
 * WHY THE ANSWER IS A BRIDGE AND NOT A RENAME. The obvious fix is to make ECH blocks carry
 * repo types. They cannot: the type IS the Content Builder id, that id is how the block is
 * pulled, cached and re-rendered, and two different SFMC blocks can share one role (five hero
 * images, four disclaimers). So the role has to be derived, not assumed.
 *
 * WHERE THE FACTS COME FROM — nothing here is a new opinion:
 *   · the block's SFMC name is on the instance as `__ech.name`, with `echNameOf()` as the
 *     fallback when a doc was rebuilt without it;
 *   · WHICH FIELDS HOLD AN IMAGE is read off the block's own guardrail (`declared[k].type
 *     === "image"`) — the same declaration the canvas uses to decide it may drop an empty
 *     `<img>`. Nothing is guessed from a field NAME;
 *   · WHICH FIELDS ARE A BUTTON comes from `ECH_CTA_PAIRS`, which already maps url→label per
 *     block and was verified against each block's guardrail when it was written.
 *
 * A block this does not recognise contributes nothing rather than a guess — same discipline as
 * the module option map. An empty cell costs Katy one lookup; a wrong one sends creative to
 * source the wrong photo.
 */
import { echMeta, echNameOf } from "./echRegistry";
import { ECH_CTA_PAIRS } from "./echDyn";
import { isHeadlineBanner } from "./singletonBlocks";

type Data = Record<string, unknown>;
const S = (v: unknown): string => (v == null ? "" : String(v));
const trim = (v: unknown): string => S(v).trim();

/** one block instance as visualSpec sees it, plus the `__ech` stamp the assembler adds */
export interface EchAware { type: string; data?: Data; __ech?: { name?: string } }

/** the SFMC block name for an instance, or "" when it is not an ECH block */
export function echNameFor(b: EchAware): string {
  const stamped = trim(b && b.__ech && b.__ech.name);
  if (stamped) return stamped;
  return echNameOf((b && b.type) || "");
}

/**
 * The blocks that own the ONE big photo at the top of the email.
 *
 * Layne ships five of these — the same hero slot with different baked headline art — so they
 * all resolve to the single "Hero Image" column rather than five columns nobody would map.
 * Kept as an explicit list, not a name pattern: `ECH_SocialIcons` also holds images and is
 * emphatically not a hero.
 */
const HERO_BLOCKS = new Set([
  "ECH_MainImage", "ECH_MainImage600", "ECH_ComingSoonImage", "ECH_AvailableNowImage",
  "ECH_JustLaunchedImage", "ECH_FindDreamHomeImage", "ECH_VIPImage",
].map((n) => n.toLowerCase()));

/** every image the block's OWN guardrail says it holds, in declaration order */
export function echImagesOf(b: EchAware): { key: string; url: string; alt: string }[] {
  const name = echNameFor(b);
  if (!name) return [];
  const meta = echMeta((b && b.type) || "");
  const declared = (meta && meta.declared) || {};
  const d = (b && b.data) || {};
  /* ALT COMES FROM THE BLOCK'S OWN ALT FIELD, PAIRED BY POSITION. `ECH_MainImage` declares
     `imgSrc` (image) and `imgAlt` (text) — so `<imageKey>Alt` would look for `imgSrcAlt` and
     find nothing. Rather than guess at a naming rule, take the declared keys ENDING in "alt"
     in declaration order and pair them with the image keys in the same order, which is how
     every one of Layne's multi-image blocks is authored. Unpaired ⇒ empty, never borrowed
     from another image: a duplicated alt ships wrong text silently, an empty one is a gap
     someone can see. */
  const keys = Object.keys(declared);
  const imgKeys = keys.filter((k) => String((declared[k] || {}).type || "").toLowerCase() === "image");
  /* ── AN ALT KEY MAY CARRY AN INDEX (2026-08-28) ───────────────────────────────
     `/alt$/` matched `imgAlt` and missed `imgAlt1` / `imgAlt2` — the exact naming
     `ECH_SecondaryImage` uses — so that block reported two photos with NO alt text at
     all, silently. Measured while wiring its columns for Katie: the URLs landed, both
     alts came back undefined. A trailing index is part of the pairing, not a different
     kind of field. */
  const altKeys = keys.filter((k) => /alt\d*$/i.test(k));
  const out: { key: string; url: string; alt: string }[] = [];
  imgKeys.forEach((k, i) => {
    const url = trim(d[k]);
    /* an empty declared image is the NORMAL state of a block the requester left alone —
       skip it rather than writing a blank cell over a real one from another block */
    if (!url || url.slice(0, 5) === "data:") return;
    out.push({ key: k, url, alt: trim(d[altKeys[i]]) });
  });
  return out;
}

/** true when this block's images belong in the Hero Image column */
export function echIsHero(b: EchAware): boolean {
  const name = echNameFor(b);
  /* + any headline banner by its guardrail module (2026-10-06, We're Open / Last Chance /
     Floorplans) — see singletonBlocks.ts isHeadlineBanner */
  return HERO_BLOCKS.has(name.toLowerCase()) || (!!name && isHeadlineBanner((b && b.type) || "", name));
}

/**
 * The buttons on an ECH block, in the order its guardrail declares them.
 *
 * `ECH_CTA_PAIRS` is url→label. A pair with a URL and no label is still a button (the block
 * renders its own default text), so the URL alone is enough to report one; a label with no URL
 * is not — that is a caption, and filing it as a CTA would tell creative to build a link that
 * was never asked for.
 */
export function echCtasOf(b: EchAware): { label: string; url: string }[] {
  const pairs = ECH_CTA_PAIRS[echNameFor(b)];
  if (!pairs) return [];
  const d = (b && b.data) || {};
  const out: { label: string; url: string }[] = [];
  for (const urlKey of Object.keys(pairs)) {
    const url = trim(d[urlKey]);
    if (!url || url === "#") continue;
    out.push({ label: trim(d[pairs[urlKey]]), url });
  }
  return out;
}

/**
 * The blocks whose buttons ARE the email's standalone buttons, the ones Katy's
 * `CTA 1 — Label/URL` and `CTA 2 — Label/URL` columns describe.
 *
 * ── WHY THIS IS NOT `ECH_CTA_PAIRS` (2026-09-24, Justin Bradford / Lauren Gleim, FNO
 * 68759: "built with 1 button, but 2 buttons were pulled into Airtable") ──────────────
 * `ECH_CTA_PAIRS` lists every block with a button, and that includes the Featured Plan
 * cards, because the FILL needs their button fields. The CTA columns walked that same
 * list, so a one-button email with plan cards filed the first card's "View Floorplan" as
 * CTA 2 (and, with the cards above the button, as CTA 1). Katy's own definition of
 * CTA 2 is "the second button on the standalone button block, empty when the block is
 * set to one button". The card buttons already have their own column
 * (`Featured Plan/Community Module`).
 *
 * An explicit list, same discipline as the module map: a new button block has to be
 * added here on purpose rather than slipping into the CTA columns by name.
 */
const CTA_COLUMN_BLOCKS = new Set(["ECH_1CTA", "ECH_2CTA", "ECH_GOTY1CTA", "ECH_GOTY2CTA", "ECH_GOInvite1CTA", "ECH_GOInvite2CTA"]);

/** true when this block's buttons belong in the CTA 1 / CTA 2 columns */
export function echIsCtaColumnBlock(b: EchAware): boolean {
  return CTA_COLUMN_BLOCKS.has(echNameFor(b));
}

/**
 * ECH block name → the Airtable "Module N" option, verbatim.
 *
 * Same 24-option list the repo-type map was harvested against (Projects fldAvjnBpLpOCmLvC,
 * read 2026-08-25) and the same rule: an unmapped block emits NOTHING. These are singleSelect
 * with typecast off, so one invented value 422s the whole 144-field write.
 *
 * Structure blocks — Header, Disclaimer, Social Icons, Divider, Contact Us — are deliberately
 * absent: they are in every email and say nothing about what it IS.
 *
 * Every name here was read off the live block list (`/sfmc-blocks?op=list&deep=1`, 61 blocks,
 * 2026-08-25) rather than inferred — a first pass invented `ECH_MapDirections`, which does not
 * exist, and would have sat here doing nothing forever.
 */
export const ECH_MODULE_OPTION: Record<string, string> = {
  ECH_ListingTable: "Listing Table",
  /* the amenity block answers to BOTH spellings: the live folder serves `ECH_Amenities`, the
     pulled fixture still carries `ECH_4Amenities`. Mapping only the current one would have
     read as "the fix does not work" against any doc built before the rename. */
  ECH_Amenities: "Amenities",
  ECH_4Amenities: "Amenities",
  ECH_Featured1CTA: "Featured Plan/Community",
  ECH_Featured2CTA: "Featured Plan/Community",
  ECH_GetDirections: "Map / Address",
  /* Katy, 2026-09-09, on a Grand Opening Invite row: "Could you have that come over as
     Module 1 - Events. Module 2 - Map / Address?" The invite blocks (community, day, date,
     time, RSVP) ARE the event module; they were unmapped, so the row listed only the map.
     First-appearance order puts them ahead of ECH_GetDirections in every GO template. The
     Thank-You variant is the same event after the fact, so it says Events too. */
  ECH_GOInvite1CTA: "Events",
  ECH_GOInvite2CTA: "Events",
  ECH_GOTY1CTA: "Events",
  ECH_GOTY2CTA: "Events",
};

/** the Module option for one block, or "" when it is not one */
export function echModuleOption(b: EchAware): string {
  return ECH_MODULE_OPTION[echNameFor(b)] || "";
}

/**
 * ECH block name → which of its declared TEXT fields are the words a human wrote,
 * split by the Airtable column they belong in.
 *
 * THE GAP THIS CLOSES. `visualSpec`'s `textOf` filters on the repo TYPE — `b.type ===
 * "headline" | "subheadline" | "body"` — so on an ECH document, where every block is typed
 * `sfmc_<contentId>`, it matches nothing and Headline / Subheadline / Copy Input come out
 * empty on every single build. Same class as the images and the CTAs; the CTA path already
 * routes through this bridge (`echCtasOf`) and the text path never got one, which is why the
 * body copy survived only inside `Build Spec (JSON)` — a debugging blob, not a build sheet.
 * Marketing Cloud builds these by hand off the row, so the copy has to be readable in a cell.
 *
 * AN EXPLICIT MAP, NOT A HEURISTIC. Picking text fields by name or by type would sweep up
 * button labels, image alts, listing-table cells, amenity captions, address lines and event
 * times — every one of them a declared text field, none of them body copy. Wrong text in
 * Copy Input is worse than none: creative would build from it.
 *
 * Every entry below was read off the LIVE block list (`/sfmc-blocks?op=list&deep=1`, 41
 * blocks, 2026-08-26) — the same discipline as ECH_MODULE_OPTION, whose first pass invented
 * a block that does not exist. A block not listed here contributes nothing.
 *
 * `subheadline` is deliberately empty: no block in the live list declares one. `sectionTitle`
 * on ECH_TableHeader is the section BANNER, which is structure rather than copy, so it is not
 * borrowed to fill the column.
 */
export const ECH_TEXT_KEYS: Record<string, { headline?: string[]; body?: string[] }> = {
  /* the block Layne calls the copy block: one headline, up to four paragraphs */
  ECH_CopyBlock: { headline: ["Headline"], body: ["p1", "p2", "p3", "p4"] },
  ECH_GOTY1CTA: { headline: ["Headline"], body: ["BodyCopy"] },
  ECH_GOTY2CTA: { headline: ["Headline"], body: ["BodyCopy"] },
  /* the invite blocks carry no headline of their own — CommunityName / Day / Date / Time are
     event details the row already captures elsewhere, so only the written paragraph is copy */
  ECH_GOInvite1CTA: { body: ["BodyCopy"] },
  ECH_GOInvite2CTA: { body: ["BodyCopy"] },
};

/**
 * The words on one ECH block for a given column, in the order its guardrail declares them.
 *
 * Multiple paragraphs join blank-line separated — the order they are meant to be read in, and
 * the same separator `visualSpec` uses between blocks. An empty field is skipped rather than
 * leaving a gap, so a Copy Block using two of its four paragraphs reads as two paragraphs.
 */
export function echTextOf(b: EchAware, slot: "headline" | "body"): string {
  const keys = (ECH_TEXT_KEYS[echNameFor(b)] || {})[slot];
  if (!keys) return "";
  const d = (b && b.data) || {};
  return keys.map((k) => trim(d[k])).filter(Boolean).join("\n\n");
}

/**
 * WHAT FILLS `Event Information` (2026-08-26).
 *
 * Katy, testing the Grand Opening Invite template on UTH: "none of the event information
 * came over either" — pointing at the block's navy panel, the one carrying the day, the
 * time and the two address lines under "You're Invited".
 *
 * Same cause as the blank image and CTA columns this module already fixes. `visualSpec`
 * fills that column from `first("event") || first("livecalendar")` — REPO types. An ECH
 * block is typed `sfmc_<contentId>`, so neither ever matched and the column was never
 * written on any build assembled from Layne's blocks.
 *
 * Read off the live block list 2026-08-26. Both invite blocks declare the same six event
 * fields; `ECH_GOTY1CTA` (the thank-you) declares none, so it is deliberately absent
 * rather than mapped to nothing.
 *
 * WHY `CommunityName` / `MasterplanName` ARE NOT HERE: the row already carries those as
 * their own linked columns, resolved server-side from Step 1. Copying the block's text
 * version into a second cell invites two answers to one question — and creative reads the
 * linked one.
 */
export const ECH_EVENT_KEYS: Record<string, string[]> = {
  ECH_GOInvite1CTA: ["Day", "Date", "Time", "AddressLine1", "AddressLine2"],
  ECH_GOInvite2CTA: ["Day", "Date", "Time", "AddressLine1", "AddressLine2"],
};

/**
 * The event details on one ECH block, one per line in declaration order.
 *
 * Line-per-field rather than blank-line separated: these are short facts read down a cell,
 * not paragraphs. An empty field is skipped, so an invite with no second address line reads
 * as three lines instead of leaving a hole.
 */
export function echEventOf(b: EchAware): string {
  const keys = ECH_EVENT_KEYS[echNameFor(b)];
  if (!keys) return "";
  const d = (b && b.data) || {};
  return keys.map((k) => trim(d[k])).filter(Boolean).join("\n");
}

/**
 * WHICH NAMED IMAGE COLUMN AN ECH BLOCK'S PHOTOS BELONG IN (2026-08-26).
 *
 * Katy, testing Available Now: "the plan information is mapping to the 'other blocks' now"
 * — the two Featured Plan photos filed as `Other Block Image 1` and `2` instead of
 * `Featured Community Image 1` and `2`.
 *
 * `collectImages` routes the repo's own blocks by type (`community` → Featured Community,
 * `map` → Map Image) but had only hero-or-spillover for ECH blocks, so every photo off one
 * of Layne's blocks landed in the three-slot overflow. Three featured cards would have
 * consumed it entirely and a fourth image anywhere in the email would have had nowhere left
 * to go — the column names exist precisely so creative does not have to read "Used In".
 *
 * Explicit map, same discipline as ECH_MODULE_OPTION: an unlisted block keeps the spillover
 * rather than being guessed into a column creative reads as authoritative. `floorplan` and
 * `checkerboard` are declared because their columns exist and Layne has said those blocks are
 * coming; no live block claims them today, which is the honest state — an empty branch beats
 * a guess when the block arrives.
 */
export type EchSlotRole = "featured" | "floorplan" | "checkerboard" | "map" | "secondary" | "";

const ECH_SLOT_ROLE: Record<string, EchSlotRole> = {
  ECH_Featured1CTA: "featured",
  ECH_Featured2CTA: "featured",
  ECH_GetDirections: "map",
  /* ── THE SECONDARY IMAGES HAVE THEIR OWN COLUMNS (2026-08-28) ──────────────────
     Katie McCarthy, on today's standup: the secondary image mapping needs review. Same
     defect as the Featured photos she reported on 08-27, one block over — `Secondary
     Image 1` and `2` exist in the intake row and nothing routed to them, so this
     block's two photos landed in `Other Block Image`, competing for a three-slot
     overflow with everything else that has no home. The block ships in Grand Opening
     Invite and Grand Opening Thank You, which is why it surfaced now. */
  ECH_SecondaryImage: "secondary",
  /* Layne's 240 px pair (2026-10-16, asset 1038758) — Katy: map it to Secondary Image 1/2
     and retire Other Block Images. Both spellings: the block may arrive with or without
     the ECH_ prefix. */
  ECH_SecondaryImages240: "secondary",
  SecondaryImages240: "secondary",
};

/** the named image column family for a block, or "" for the spillover */
export function echSlotRole(b: EchAware): EchSlotRole {
  return ECH_SLOT_ROLE[echNameFor(b)] || "";
}
